import React, { useState, useEffect, useRef } from "react";
import { ExpandMore, FilterNoneOutlined } from "@material-ui/icons";
import { 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import WaveSurfer from "wavesurfer.js";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import audioIcon from "../../assets/miscPng/audio-waves.png";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "1em",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s",
  },

  deletedMediaMessage: {
    opacity: 0.7,
    filter: "grayscale(80%)",
  },

  audioContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-around",
  },

  audioTranscriptContainer: {
    maxWidth: "250px",
    borderLeft: "5px solid #7159c1",
    backgroundColor: "rgba(113, 89, 193, 0.25)",
    borderRadius: "5px",
    
    "& .MuiAccordionSummary-expandIcon": {
      color: theme.palette.text.primary,
    },
  },

  audioTranscript: {
    textAlign: "justify",
    textIndent: "1em",
  },

  audioAvatar: {
		margin: 15,
		width: 35,
		height: 35,
    padding: 3.5,
		cursor: "default",
	},

  floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)" },
  },

  icon: {
		color: theme.palette.text.primary,
	},

  actionButton: {
    "&:hover": { color: theme.palette.primary.main },
  },

  waveformContainer: {
    width: "100.00%",
    cursor: "grab",
    whiteSpace: "normal",
    zIndex: 0,
  },
}));

const Audio = ({
  currentAudioRef, transcript, url, setAudioPlayer, setCurrentPlayingAudioTime, ticket,
  messageId, isFromStarredMessages=false, currentTicketId=null, isDeleted=false,
  isVoice, isFromMe, isQuotedMessage=false
}) => {
  //  ***************
  //  ** Variables **
  //  ***************
  let avatar;
  const LS_NAME = "audioMessageRate";

  const classes = useStyles();
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const waveformRef = useRef(null);
  
  const timeoutAudioRef = useRef(null);
  const timeoutCopyRef = useRef(null);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [audioRate, setAudioRate] = useState(1.00);
  const [showButtonRate, setShowButtonRate] = useState(false);

  const [transcriptionAccordionOpen, setTranscriptionAccordionOpen] = useState(true);
  const [loadingTranscript, setLoadingTranscript] = useState(false);

  const [isTranscriptCopied, setIsTranscriptCopied] = useState(false);
  


  //  *****************
  //  ** Assignments **
  //  *****************
  if (isVoice && isFromMe) avatar = undefined;
  else if (isVoice && ticket?.contact) avatar = ticket.contact.profilePicUrl;
  else if (isVoice) avatar = undefined;
  else avatar = audioIcon;



  //  ***************
  //  ** Functions **
  //  ***************
  const toogleRate = () => {
    setAudioRate(previousValue => {
      if (previousValue === 2.00) return 0.25;
      return previousValue + 0.25;
    });
  };

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    audioRef.current.playbackRate = audioRate;
    localStorage.setItem(LS_NAME, audioRate);
  }, [audioRate]);

  useEffect(() => {
    if (ticket !== undefined) {
      audioRef.current.onplay = () => {
        if (currentAudioRef.current) currentAudioRef.current.pause();

        if (typeof setAudioPlayer === "function") {
          setAudioPlayer(
            url,
            audioRef.current.currentTime,
            currentTicketId ? currentTicketId.toString() : ticket.id.toString(),
            ticket.contact?.profilePicUrl,
            ticket.contact?.name || ticket.noteName,
            ticket.type === 0 ? ticket.contact?.id.toString() : "0"
          );
        }

        if (timeoutAudioRef.current) clearTimeout(timeoutAudioRef.current);
        timeoutAudioRef.current = setTimeout(() => currentAudioRef.current = audioRef.current, 100);
      };

      audioRef.current.onplaying = () => {
        setShowButtonRate(true); 
        intervalRef.current = setInterval(() => { 
          try {
            setCurrentPlayingAudioTime(audioRef.current.currentTime);
            localStorage.setItem("currentPlayingAudioTime", audioRef.current.currentTime);
          }
          catch (exception) {
            clearInterval(intervalRef.current);
          }
        }, 1000);
      };

      audioRef.current.onpause = () => {
        setShowButtonRate(false);
        currentAudioRef.current = null;
        clearInterval(intervalRef.current);
      };

      audioRef.current.onended = () => {
        if (typeof setAudioPlayer === "function") setAudioPlayer("", "", "", "", "", "");
        setShowButtonRate(false);
        currentAudioRef.current = null;
        clearInterval(intervalRef.current);
      };
    }

    return () => {
      clearInterval(intervalRef.current);
      if (timeoutAudioRef.current) clearTimeout(timeoutAudioRef.current);
    }
  }, [url, setAudioPlayer, setCurrentPlayingAudioTime, ticket, currentAudioRef, currentTicketId]);

  useEffect(() => {
    if (isSmallScreen) return;

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,

      waveColor: "#a8a8a8",
      progressColor: "#8c8888",
      cursorColor: "#34b7f1",
      cursorWidth: 5,
      scrollParent: false,

      height: 50,
      barWidth: 2,
      barRadius: 2,
      barGap: null,
      
      responsive: true,
      normalize: true,

      interact: true,
      dragToSeek: true,
      
      url: audioRef.current.src,
      backend: "MediaElement",
      media: audioRef.current,
    });

    return () => wavesurfer.destroy();
  }, [isSmallScreen]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleTranscribeAudio = async () => {
    try {
      setLoadingTranscript(true);

      await api.get(`/openAi/transcribeAudio/${messageId}`, {
        params: { audioUrl: url, currentTicketId, ticketType: ticket.type }
      });
    } catch (exception) {
      toastError(exception);
    } finally {
      setLoadingTranscript(false);
    }
  };

  const handleCopyTranscript = async (transcript) => {
    setIsTranscriptCopied(true);
    await navigator.clipboard.writeText(transcript);

    if (timeoutCopyRef.current) clearTimeout(timeoutCopyRef.current);
    timeoutCopyRef.current = setTimeout(() => setIsTranscriptCopied(false), 500);
  };

  const handlePause = () => {
    if (typeof setAudioPlayer !== "function") return;
    setAudioPlayer("", "", "", "", "", "")
  };
  


  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={isDeleted ? `${classes.mainContainer} ${classes.deletedMediaMessage}` : classes.mainContainer}>
      <div className={classes.audioContainer}>
        <audio
          style={{ width: isSmallScreen ? "230px" : "300px" }}
          onPause={handlePause}
          ref={audioRef}
          src={url}
          controls
        />

        {!isFromStarredMessages && ticket && (
          <Button disabled={!showButtonRate} onClick={toogleRate}>
            {audioRate}<span>x</span>
          </Button>
        )}

        {!isSmallScreen && (
          <>        
            <Avatar src={avatar} className={classes.audioAvatar} />
            <div ref={waveformRef} className={classes.waveformContainer} />
          </>
        )}
      </div>
      
      {messageId && !isQuotedMessage && (
        <Button
          className={classes.floatingButton}
          color="inherit"
          variant="outlined"
          disabled={!url}
          onClick={async () => await handleTranscribeAudio()}
        >
          {i18n.t("audioComponent.accordionTitle")}
        </Button>
      )}

      {loadingTranscript && !transcript && ( <CircularProgress size={24} /> )}
      
      {messageId && transcript && (
        <>
          <Accordion 
            className={classes.audioTranscriptContainer}
            expanded={transcriptionAccordionOpen} 
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              TransitionProps={{ unmountOnExit: true }}
              onClick={() => setTranscriptionAccordionOpen((prevValue) => !prevValue )}
            >
              <Typography className={classes.accordionHeading}>
                🤫 {i18n.t("audioComponent.accordionTitle")}
              </Typography>
            </AccordionSummary>

            <AccordionDetails className={classes.audioTranscript}>
              <span>{transcript}</span>
            </AccordionDetails>
          </Accordion>

          <Tooltip 
            title={isTranscriptCopied ? i18n.t("audioComponent.tooltips.copied") : i18n.t("audioComponent.tooltips.copy") }
            placement="top-start"
            arrow
          >
            <IconButton
              className={`${classes.icon} ${classes.floatingButton}`}
              size="small"
              onClick={async () => await handleCopyTranscript(transcript)}
            >
              <FilterNoneOutlined className={classes.actionButton} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </div>
  );
}

export default Audio;